import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { Box, Dialog, DialogContent, CircularProgress, Stack, Typography, Checkbox, DialogActions } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';

ParentFacilityList.propTypes = {
  parentInEdit: PropTypes.object,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function ParentFacilityList({ parentInEdit, open, close }) {
  const gridRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [loadingParentFacilities, setLoadingParentFacilities] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    if (parentInEdit && open) {
      setLoadingParentFacilities(true);
      axios.get(`/parent/ParentFacilities?id=${parentInEdit.id}`)
        .then((res) => {
          const parentFacilities = res.data ?? [];
          setFacilities(parentFacilities);
          if (parentFacilities.length === 0) enqueueSnackbar('No facilities were found for this parent.', { variant: 'warning' });
        })
        .catch(() => enqueueSnackbar('Get parent facilities failed', { variant: 'error' }))
        .finally(() => setLoadingParentFacilities(false));
    }
  }, [open, parentInEdit?.id]);

  const updateRowOnHold = (params, onHold) => {
    const newNodeData = { ...params.node.data, onHold };
    setFacilities((currentFacilities) => currentFacilities.map((facility) => (facility.id === newNodeData.id ? newNodeData : facility)));
  };

  const updateAllRowOnHold = (params, checked) => {
    const nextValue = !checked;
    const updateIds = [];
    params.api.forEachNodeAfterFilter((node) => {
      node.data.onHold = nextValue;
      updateIds.push(node.data.id);
    });
    setFacilities((currentFacilities) => currentFacilities.map((facility) => (updateIds.includes(facility.id) ? { ...facility, onHold: nextValue } : facility)));
  };

  const save = () => {
    if (!parentInEdit?.id) return;
    setSubmitting(true);

    axios.post('/parent/parentFacilitiesForm', objectToFormData({ parentId: parentInEdit.id, facilities: facilities.map((facility) => ({ id: facility.id, onHold: facility.onHold})) }))
      .then((res) => {
        if (res.data) { enqueueSnackbar('Parent facilities updated', { variant: 'success' }); close(); }
        else enqueueSnackbar('Update parent facilities failed', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar('Update parent facilities failed', { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Facility',
      minWidth: 200,
      flex: 1
    },
    {
      field: 'facilityCode',
      headerName: 'Facility Code',
      minWidth: 150
    },
    {
      field: 'purchasingGroupName',
      headerName: 'Purchasing Group',
      minWidth: 180
    },
    {
      field: 'onHold',
      headerName: 'On Hold',
      width: 150,
      cellRenderer: (params) => <Checkbox checked={params.data.onHold} onChange={(e) => updateRowOnHold(params, e.target.checked)} />,
      headerComponent: (params) => AGGridHeaderCheckboxSimple(params, updateAllRowOnHold)
    }
  ];

  return <Dialog open={open} onClose={close} maxWidth="xl" fullWidth PaperProps={{ sx: { mt: 1 } }}>
    <DialogContent>
      <Stack spacing={1}>
        {parentInEdit && <Typography variant="h3" textAlign="center">{parentInEdit.name}</Typography>}
        <AGGridQuickFilterWrapper gridRef={gridRef} />
        <Box>
          {!loadingParentFacilities ? <Box sx={{ height: 'clamp(400px, calc(100vh - 300px), 650px)' }}>
            <AgGridReact
              ref={gridRef}
              {...AgGridDefaults}
              columnDefs={columns}
              rowData={facilities}
              onFilterChanged={(params) => params.api.refreshHeader()}
            />
          </Box> : <Box sx={{ display: 'flex', justifyContent: 'center', m: 3 }}><CircularProgress /></Box>}
        </Box>
        {facilities.length > 0 && <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained" onClick={save}>
            Save
          </LoadingButton>
        </Box>}
      </Stack>
    </DialogContent>
  </Dialog>;
}